<template>
  <div class="insights-dimensions">
    <h2 class="insights-dimensions__title">Confidence breakdown by dimension</h2>
    <p class="insights-dimensions__intro">
      For each dimension (Account, Department, …), what share of proposals landed at each
      confidence level. Calculated at <strong>invoice-line level</strong> — every line on
      every invoice in the period counts once per dimension.
      <q-icon name="help_outline" size="14px" class="insights-dimensions__hint-icon">
        <q-tooltip max-width="320px">
          <div style="margin-bottom: 6px;">
            <strong>High</strong> — confident enough to post automatically.<br />
            <strong>Medium</strong> — a bookkeeper should glance at the suggestion.<br />
            <strong>Low</strong> — a bookkeeper should review carefully.
          </div>
          The <strong>Combination</strong> row at the bottom is the share where <em>every</em>
          dimension on the line was at that confidence at once — not just one of them.
        </q-tooltip>
      </q-icon>
    </p>

    <q-skeleton v-if="loading" animation="blink" height="280px" />
    <div v-else-if="!rows.length" class="insights-dimensions__empty">
      <q-icon name="inbox" size="32px" color="blue-grey-2" />
      <span>No data for this period.</span>
    </div>
    <div v-else class="insights-dimensions__table">
      <div class="insights-dimensions__header">Dimension</div>
      <div class="insights-dimensions__header">High</div>
      <div class="insights-dimensions__header">Medium</div>
      <div class="insights-dimensions__header">Low</div>

      <template v-for="row in regularRows" :key="row.dimensionName">
        <div class="insights-dimensions__cell">{{ row.dimensionName }}</div>
        <div class="insights-dimensions__cell">{{ row.highPerformancePercentage.toFixed(1) }}%</div>
        <div class="insights-dimensions__cell">{{ row.mediumPerformancePercentage.toFixed(1) }}%</div>
        <div class="insights-dimensions__cell">{{ row.lowPerformancePercentage.toFixed(1) }}%</div>
      </template>

      <template v-if="combinationRow">
        <div class="insights-dimensions__cell insights-dimensions__cell--total">{{ combinationRow.dimensionName }}</div>
        <div class="insights-dimensions__cell insights-dimensions__cell--total">{{ combinationRow.highPerformancePercentage.toFixed(1) }}%</div>
        <div class="insights-dimensions__cell insights-dimensions__cell--total">{{ combinationRow.mediumPerformancePercentage.toFixed(1) }}%</div>
        <div class="insights-dimensions__cell insights-dimensions__cell--total">{{ combinationRow.lowPerformancePercentage.toFixed(1) }}%</div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { InsightsDimensionsResult } from '../types';

const props = defineProps<{
  data: InsightsDimensionsResult | null;
  loading: boolean;
}>();

const rows = computed(() => props.data?.dimensionPerformances ?? []);

const regularRows = computed(() =>
  rows.value
    .filter(r => r.dimensionName !== 'Combination')
    .sort((a, b) => b.highPerformancePercentage - a.highPerformancePercentage)
);

const combinationRow = computed(() =>
  rows.value.find(r => r.dimensionName === 'Combination') ?? null
);
</script>

<style scoped lang="scss">
.insights-dimensions {
  &__title {
    margin: 0 0 4px;
    font-size: 18px;
    font-weight: 600;
    color: #2c2c2c;
  }

  &__intro {
    margin: 0 0 12px;
    font-size: 13px;
    color: #4c4c4c;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  &__hint-icon {
    cursor: help;
    color: #8b8b8b;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 240px;
    color: #8b8b8b;
    font-size: 13px;
  }

  &__table {
    display: grid;
    grid-template-columns: minmax(140px, 1.4fr) repeat(3, minmax(70px, 1fr));
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    overflow: hidden;
  }

  &__header,
  &__cell {
    padding: 8px 12px;
    font-size: 13px;
    line-height: 1.3;
    border-bottom: 1px solid #f0f0f0;
  }

  &__header {
    background: #f5f5f5;
    font-weight: 700;
    font-size: 11px;
    text-transform: uppercase;
    color: #626262;
  }

  &__cell--total {
    font-weight: 700;
    background: #fafafa;
    border-top: 1px solid #e0e0e0;
  }
}
</style>
